import { useState } from "react";
import { Info, Minus, Package, Plus, Trash2 } from "lucide-react";

const money = (value) =>
  `₹${Number(value || 0).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

const formatRuleGroup = (value) =>
  String(value || "")
    .replace(/_/g, " ")
    .toLowerCase()
    .replace(/\b\w/g, (letter) => letter.toUpperCase());

function GstCell({ item, isReturn }) {
  const taxRate =
    item.taxRate === null || item.taxRate === undefined
      ? null
      : Number(item.taxRate);

  if (isReturn) {
    return (
      <div className="text-center">
        <p className="font-semibold text-red-700">
          {taxRate === null ? "—" : `${taxRate.toFixed(2)}%`}
        </p>
        <p className="mt-0.5 text-[10px] text-red-500">Original sale GST</p>
      </div>
    );
  }

  if (taxRate !== null) {
    return (
      <div className="text-center">
        <p className="font-semibold text-primary-800">
          {taxRate.toFixed(2)}%
        </p>
        <p className="mt-0.5 text-[10px] text-slate-500">Final GST</p>
      </div>
    );
  }

  return (
    <div className="text-center">
      <p className="font-semibold text-slate-700">Auto</p>
      <p className="mt-0.5 text-[10px] text-slate-500">
        {item.gstRuleGroup
          ? `${formatRuleGroup(item.gstRuleGroup)} rule`
          : "At payment"}
      </p>
    </div>
  );
}

export default function PosBillItemsTable({
  billItems,
  canOverridePrice,
  onQuantityChange,
  onPriceChange,
  onPriceOverrideReasonChange,
  agents,
  onAgentChange,
  onRemove,
  onAgentSearch,
  lotAllocationMethod,
  onLotChange,
}) {
  const [activeAgentRowId, setActiveAgentRowId] = useState(null);
  const [agentQueries, setAgentQueries] = useState({});

  const quantity = billItems.reduce(
    (sum, item) => sum + Number(item.quantity || 0),
    0,
  );

  const signedTotal = billItems.reduce((sum, item) => {
    const amount = Number(item.unitPrice || 0) * Number(item.quantity || 0);
    return sum + (item.lineType === "RETURN" ? -amount : amount);
  }, 0);

  return (
    <div className="overflow-hidden border border-slate-300 bg-white shadow-sm">
      <div className="flex items-center justify-between border-b border-slate-300 bg-slate-100 px-4 py-2">
        <div>
          <p className="text-sm font-bold uppercase tracking-wide text-primary-800">
            Sale Bill Items
          </p>

          <p className="mt-0.5 text-xs text-slate-500">
            Add new products and verified return items in the same bill.
          </p>
        </div>

        <div className="flex items-center gap-4 text-xs font-semibold text-slate-600">
          <span>
            Lines: <b className="text-primary-800">{billItems.length}</b>
          </span>

          <span>
            Qty: <b className="text-primary-800">{quantity}</b>
          </span>

          <span>
            Net:{" "}
            <b
              className={signedTotal < 0 ? "text-red-700" : "text-primary-800"}
            >
              {signedTotal < 0 ? "-" : ""}
              {money(Math.abs(signedTotal))}
            </b>
          </span>
        </div>
      </div>

      <div className="max-h-[560px] min-h-[420px] overflow-auto">
        <table className="w-full min-w-[1690px] border-collapse text-sm">
          <thead className="sticky top-0 z-20 bg-slate-100">
            <tr className="border-b border-slate-300 text-left text-[11px] font-bold uppercase tracking-wide text-slate-700">
              <th className="w-12 border-r border-slate-300 px-2 py-3 text-center">
                S.No.
              </th>
              <th className="w-24 border-r border-slate-300 px-3 py-3">Type</th>
              <th className="w-32 border-r border-slate-300 px-3 py-3">SKU</th>
              <th className="min-w-[260px] border-r border-slate-300 px-3 py-3">
                Item Name
              </th>
              <th className="w-28 border-r border-slate-300 px-3 py-3">
                Source Bill
              </th>
              <th className="w-44 border-r border-slate-300 px-3 py-3">
                Stock Lot
              </th>
              <th className="w-20 border-r border-slate-300 px-3 py-3">Size</th>
              <th className="w-24 border-r border-slate-300 px-3 py-3">
                Color
              </th>
              <th className="w-32 border-r border-slate-300 px-2 py-3 text-center">
                GST
              </th>
              <th className="w-36 border-r border-slate-300 px-2 py-3 text-center">
                Quantity
              </th>
              <th className="w-28 border-r border-slate-300 px-3 py-3">
                Season
              </th>
              <th className="w-28 border-r border-slate-300 px-3 py-3 text-right">
                Sale Rate
              </th>
              <th className="w-32 border-r border-slate-300 px-3 py-3 text-right">
                Amount
              </th>
              <th className="w-48 border-r border-slate-300 px-3 py-3">
                Sales Agent
              </th>
              <th className="w-16 px-2 py-3 text-center">Delete</th>
            </tr>
          </thead>

          <tbody>
            {!billItems.length ? (
              <tr>
                <td colSpan={15} className="h-[360px] text-center">
                  <Package size={48} className="mx-auto text-slate-300" />

                  <h3 className="mt-4 text-base font-semibold text-slate-700">
                    No billing items
                  </h3>

                  <p className="mt-1 text-sm text-slate-500">
                    Scan a new item, or select an eligible old sale item as a
                    return.
                  </p>
                </td>
              </tr>
            ) : (
              billItems.map((item, index) => {
                const isReturn = item.lineType === "RETURN";
                const lineAmount =
                  Number(item.unitPrice || 0) * Number(item.quantity || 0);

                return (
                  <tr
                    key={item.rowId}
                    className={`border-b border-slate-200 ${
                      isReturn
                        ? "bg-red-50/70 hover:bg-red-100/70"
                        : "hover:bg-primary-50/40"
                    }`}
                  >
                    <td className="border-r border-slate-200 px-2 py-2 text-center font-semibold">
                      {index + 1}
                    </td>

                    <td className="border-r border-slate-200 px-3 py-2">
                      <span
                        className={`rounded px-2 py-1 text-[10px] font-bold ${
                          isReturn
                            ? "bg-red-100 text-red-700"
                            : "bg-emerald-100 text-emerald-700"
                        }`}
                      >
                        {isReturn ? "RETURN" : "SALE"}
                      </span>
                    </td>

                    <td className="border-r border-slate-200 px-3 py-2">
                      <span className="font-mono text-xs font-semibold text-primary-800">
                        {item.sku}
                      </span>

                      {item.barcode && (
                        <p className="mt-1 text-[10px] text-slate-400">
                          {item.barcode}
                        </p>
                      )}
                    </td>

                    <td className="border-r border-slate-200 px-3 py-2">
                      <p className="font-semibold text-slate-800">
                        {item.productName}
                      </p>

                      <p className="mt-0.5 text-xs text-slate-500">
                        {item.variantName || "Default Variant"}
                      </p>
                    </td>

                    <td className="border-r border-slate-200 px-3 py-2 text-xs">
                      {isReturn ? (
                        <span className="font-mono font-semibold text-red-700">
                          {item.receiptNumber || "—"}
                        </span>
                      ) : (
                        <span className="text-slate-400">New sale</span>
                      )}
                    </td>

                    <td className="border-r border-slate-200 px-3 py-2">
                      {isReturn ? (
                        <span className="text-xs text-slate-400">Original sale</span>
                      ) : lotAllocationMethod === "NORMAL" ? (
                        <select
                          value={item.inventoryLotId || ""}
                          onChange={(event) =>
                            onLotChange?.(item.rowId, event.target.value)
                          }
                          className="w-full rounded-lg border border-slate-300 bg-white px-2 py-2 text-xs font-medium text-slate-700 outline-none focus:border-primary-800"
                        >
                          {(item.availableLots || []).map((lot) => (
                            <option key={lot.id} value={lot.id}>
                              {lot.lotNumber} · ₹{Number(lot.posPrice).toFixed(2)} · {lot.availableQuantity} left
                            </option>
                          ))}
                        </select>
                      ) : (
                        <div>
                          <p className="font-mono text-xs font-semibold text-primary-800">
                            FIFO
                          </p>
                          <p className="mt-0.5 text-[10px] text-slate-500">
                            Oldest priced lot first
                          </p>
                        </div>
                      )}
                    </td>

                    <td className="border-r border-slate-200 px-3 py-2">
                      {item.size?.name || "—"}
                    </td>

                    <td className="border-r border-slate-200 px-3 py-2">
                      <span className="inline-flex items-center gap-1.5">
                        {item.color?.hexCode && (
                          <span
                            className="h-3 w-3 rounded-full border border-slate-300"
                            style={{ backgroundColor: item.color.hexCode }}
                          />
                        )}
                        {item.color?.name || "—"}
                      </span>
                    </td>

                    <td className="border-r border-slate-200 px-2 py-2">
                      <GstCell item={item} isReturn={isReturn} />
                    </td>

                    <td className="border-r border-slate-200 px-2 py-2">
                      <div className="flex items-center justify-center gap-1">
                        <button
                          type="button"
                          disabled={item.quantity <= 1}
                          onClick={() =>
                            onQuantityChange(
                              item.rowId,
                              Number(item.quantity) - 1,
                            )
                          }
                          className="grid h-7 w-7 place-items-center border border-slate-300 bg-slate-50 text-slate-600 disabled:opacity-40"
                        >
                          <Minus size={13} />
                        </button>

                        <input
                          type="number"
                          min="1"
                          max={item.maxQuantity || undefined}
                          value={item.quantity}
                          onChange={(event) =>
                            onQuantityChange(item.rowId, event.target.value)
                          }
                          className="h-7 w-12 border border-slate-300 text-center text-sm font-semibold outline-none focus:border-primary-800"
                        />

                        <button
                          type="button"
                          disabled={
                            item.maxQuantity !== null &&
                            item.maxQuantity !== undefined &&
                            item.quantity >= item.maxQuantity
                          }
                          onClick={() =>
                            onQuantityChange(
                              item.rowId,
                              Number(item.quantity) + 1,
                            )
                          }
                          className="grid h-7 w-7 place-items-center border border-slate-300 bg-slate-50 text-slate-600 disabled:opacity-40"
                        >
                          <Plus size={13} />
                        </button>
                      </div>
                    </td>

                    <td className="border-r border-slate-200 px-3 py-2">
                      {item.season?.name || "—"}
                    </td>

                    <td className="border-r border-slate-200 px-2 py-2">
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        disabled={isReturn || !canOverridePrice}
                        value={item.unitPrice}
                        onFocus={(event) => {
                          if (canOverridePrice && !isReturn) {
                            event.target.select();
                          }
                        }}
                        onChange={(event) =>
                          onPriceChange(item.rowId, event.target.value)
                        }
                        className={`h-8 w-full border px-2 text-right text-sm font-semibold outline-none ${
                          isReturn
                            ? "cursor-not-allowed border-transparent bg-transparent text-red-700"
                            : canOverridePrice
                              ? "border-slate-300 bg-white focus:border-primary-800"
                              : "cursor-not-allowed border-transparent bg-transparent"
                        }`}
                      />

                      {!isReturn &&
                        Number(item.unitPrice) !== Number(item.listPrice) && (
                          <input
                            value={item.priceOverrideReason || ""}
                            onChange={(event) =>
                              onPriceOverrideReasonChange(
                                item.rowId,
                                event.target.value,
                              )
                            }
                            placeholder="Override reason *"
                            className="mt-1 h-7 w-full border border-amber-300 bg-amber-50 px-2 text-[11px] outline-none"
                          />
                        )}
                    </td>

                    <td
                      className={`border-r border-slate-200 px-3 py-2 text-right font-bold ${
                        isReturn ? "text-red-700" : "text-primary-800"
                      }`}
                    >
                      {isReturn ? "-" : ""}
                      {money(lineAmount)}
                    </td>

                    <td className="relative border-r border-slate-200 px-3 py-2">
                      {isReturn ? (
                        <span className="text-xs text-slate-600">
                          {item.agentEmployee?.fullName
                            ? `${item.agentEmployee.fullName}${
                                item.agentEmployee.employeeCode
                                  ? ` · ${item.agentEmployee.employeeCode}`
                                  : ""
                              }`
                            : "No original sale agent"}
                        </span>
                      ) : (
                        <>
                          <input
                            value={agentQueries[item.rowId] || ""}
                            onFocus={() => setActiveAgentRowId(item.rowId)}
                            onChange={(event) => {
                              const query = event.target.value;

                              setAgentQueries((previous) => ({
                                ...previous,
                                [item.rowId]: query,
                              }));

                              setActiveAgentRowId(item.rowId);
                              onAgentSearch?.(query);

                              if (!query.trim()) {
                                onAgentChange(item.rowId, "");
                              }
                            }}
                            placeholder="Search sales agent..."
                            className="w-full rounded-lg border border-slate-300 bg-white px-2 py-2 text-xs outline-none focus:border-primary-800"
                          />

                          {activeAgentRowId === item.rowId &&
                            (agentQueries[item.rowId] || "").trim() && (
                              <div className="absolute left-3 right-3 top-full z-30 mt-1 max-h-44 overflow-y-auto rounded-xl border border-slate-200 bg-white p-1 shadow-xl">
                                {agents.length ? (
                                  agents.map((agent) => (
                                    <button
                                      key={agent.id}
                                      type="button"
                                      onClick={() => {
                                        onAgentChange(item.rowId, agent.id);

                                        setAgentQueries((previous) => ({
                                          ...previous,
                                          [item.rowId]: `${agent.fullName} · ${agent.employeeCode}`,
                                        }));

                                        setActiveAgentRowId(null);
                                      }}
                                      className="block w-full rounded-lg px-3 py-2 text-left text-xs hover:bg-primary-50"
                                    >
                                      <span className="font-semibold text-slate-800">
                                        {agent.fullName}
                                      </span>

                                      <span className="ml-1 text-slate-500">
                                        · {agent.employeeCode}
                                      </span>
                                    </button>
                                  ))
                                ) : (
                                  <p className="px-3 py-2 text-xs text-slate-400">
                                    No matching agents found.
                                  </p>
                                )}
                              </div>
                            )}
                        </>
                      )}
                    </td>

                    <td className="px-2 py-2 text-center">
                      <button
                        type="button"
                        onClick={() => onRemove(item.rowId)}
                        className="grid h-7 w-7 place-items-center border border-red-200 bg-red-50 text-red-600 transition hover:bg-red-100"
                        aria-label={`Remove ${item.productName}`}
                      >
                        <Trash2 size={15} />
                      </button>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      <div className="flex items-center justify-between border-t border-slate-300 bg-slate-50 px-4 py-2 text-xs text-slate-500">
        <span className="inline-flex items-center gap-1.5">
          <Info size={13} />
          New-sale GST is confirmed after discounts at payment. Returns use original GST.
        </span>

        <span className="font-semibold">
          Quantity: <b className="text-emerald-600">{quantity} PCS</b>
        </span>
      </div>
    </div>
  );
}
